<template>
    <v-card class="leader-card rounded-lg">
        <div class="leader-card__header">
            <v-icon small color="orange">{{ statHeader.icon || 'mdi-chart-bar' }}</v-icon>
            <span class="leader-card__title">{{ statHeader.label || statHeader.text }}</span>
            <span class="leader-card__abbr">{{ statHeader.text }}</span>
        </div>
        <v-progress-linear v-if="loading" color="orange" indeterminate height="3" />

        <template v-if="!loading && stats && stats.length">
            <!-- Leader -->
            <div class="leader-card__top" @click="handleClick(leader)">
                <div class="leader-card__top-media">
                    <div v-if="!option && leader.teamLogo" class="leader-card__watermark">
                        <img :src="`${assetsUrl}/${leader.teamLogo}`" alt="">
                    </div>
                    <img
                        :src="option ? `${assetsUrl}/${leader.logo}` : `${assetsUrl}/${leader.image}`"
                        class="leader-card__top-img"
                        alt=""
                        @error="$event.target.src = defaultPlayerImg"
                    >
                </div>
                <div class="leader-card__top-info">
                    <span class="leader-card__rank leader-card__rank--first">1</span>
                    <div class="leader-card__top-name">{{ entityName(leader) }}</div>
                    <div v-if="!option && leader.team" class="leader-card__top-team">{{ leader.team }}</div>
                    <div class="leader-card__top-value">
                        {{ formatAvg(leader) }}
                        <span class="leader-card__top-meta">{{ leader.cum[statHeader.value] }} σε {{ leader.stats.length }} αγ.</span>
                    </div>
                </div>
            </div>

            <!-- Runners-up -->
            <div
                v-for="(item, index) in runnersUp"
                :key="item.id || index"
                class="leader-card__row"
                @click="handleClick(item)"
            >
                <span class="leader-card__rank">{{ index + 2 }}</span>
                <span class="leader-card__thumb">
                    <img
                        :src="option ? `${assetsUrl}/${item.logo}` : `${assetsUrl}/${item.image}`"
                        alt=""
                        @error="$event.target.src = defaultPlayerImg"
                    >
                </span>
                <span class="leader-card__row-name">
                    {{ entityName(item) }}
                    <span v-if="!option && item.team" class="leader-card__row-team">{{ item.team }}</span>
                </span>
                <span class="leader-card__row-value">{{ formatAvg(item) }}</span>
            </div>
        </template>

        <div v-else-if="!loading" class="leader-card__empty">Δεν υπάρχουν δεδομένα</div>

        <button type="button" class="leader-card__more" @click="$emit('scrollToStats')">
            Όλη η λίστα
            <v-icon small color="orange darken-2">mdi-arrow-down</v-icon>
        </button>
    </v-card>
</template>

<script>
    export default {
        props: {
            option: {
                type: Boolean,
                default: true
            },
            stats: {
                type: Array,
                default: null
            },
            statHeader: {
                type: Object,
                default: null
            },
            loading: {
                type: Boolean,
                default: true
            }
        },
        data() {
            return {
                defaultPlayerImg: require('@/assets/default_player.png'),
                assetsUrl: 'https://storage.googleapis.com'
            }
        },
        computed: {
            leader() {
                return this.stats[0]
            },
            runnersUp() {
                return this.stats.slice(1)
            }
        },
        methods: {
            entityName(item) {
                return this.option ? item.name : `${item.surname} ${item.name}`
            },
            formatAvg(item) {
                const value = Number(item?.avg?.[this.statHeader.value])
                return Number.isFinite(value) ? value.toFixed(1) : '-'
            },
            handleClick(item) {
                if (!item || !item.id) {
                    return
                }
                this.$router.push({ name: this.option ? 'Team' : 'Player', params: { id: item.id } })
            }
        }
    }
</script>

<style scoped>
.leader-card {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
    font-family: Ubuntu, Arial, sans-serif;
}

.leader-card__header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    background: #0d0d0f;
    border-bottom: 3px solid #ff7133;
    color: #fff;
}

.leader-card__title {
    flex: 1 1 auto;
    font-weight: 800;
    letter-spacing: 0.02em;
}

.leader-card__abbr {
    padding: 0 6px;
    border-radius: 4px;
    background: rgba(255, 255, 255, 0.15);
    font-size: 0.7rem;
    font-weight: 700;
}

/* ---------- Leader ---------- */
.leader-card__top {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px;
    background: linear-gradient(90deg, #ef4444 0%, #ff7133 60%, orange 100%);
    color: #fff;
    cursor: pointer;
}

.leader-card__top-media {
    position: relative;
    flex: 0 0 72px;
    width: 72px;
    height: 72px;
}

.leader-card__watermark {
    position: absolute;
    inset: -8px;
    opacity: 0.25;
}

.leader-card__watermark img {
    width: 100%;
    height: 100%;
    object-fit: contain;
}

.leader-card__top-img {
    position: relative;
    width: 100%;
    height: 100%;
    padding: 3px;
    border-radius: 10px;
    background: #fff;
    object-fit: contain;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.leader-card__top-info {
    position: relative;
    flex: 1 1 auto;
    min-width: 0;
}

.leader-card__top-name {
    font-weight: 800;
    line-height: 1.2;
    word-break: break-word;
}

.leader-card__top-team {
    font-size: 0.75rem;
    opacity: 0.85;
}

.leader-card__top-value {
    margin-top: 2px;
    font-size: 1.8rem;
    font-weight: 800;
    line-height: 1.1;
    font-variant-numeric: tabular-nums;
}

.leader-card__top-meta {
    display: block;
    font-size: 0.7rem;
    font-weight: 600;
    opacity: 0.85;
}

/* ---------- Rows ---------- */
.leader-card__row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 14px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    cursor: pointer;
    transition: background 0.15s ease;
}

.leader-card__row:hover {
    background: #fff3ec;
}

.leader-card__rank {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 6px;
    background: #f1f1f3;
    color: rgba(0, 0, 0, 0.7);
    font-size: 0.75rem;
    font-weight: 800;
}

.leader-card__rank--first {
    position: absolute;
    top: -4px;
    right: 0;
    background: #0d0d0f;
    color: #fff;
}

.leader-card__thumb {
    display: inline-flex;
    flex: 0 0 auto;
    width: 32px;
    height: 32px;
    padding: 2px;
    border-radius: 6px;
    background: #fff;
    border: 1px solid rgba(0, 0, 0, 0.1);
    overflow: hidden;
}

.leader-card__thumb img {
    width: 100%;
    height: 100%;
    object-fit: contain;
}

.leader-card__row-name {
    flex: 1 1 auto;
    min-width: 0;
    font-size: 0.85rem;
    font-weight: 700;
    line-height: 1.2;
    word-break: break-word;
}

.leader-card__row-team {
    display: block;
    color: rgba(0, 0, 0, 0.5);
    font-size: 0.7rem;
    font-weight: 500;
}

.leader-card__row-value {
    font-weight: 800;
    font-variant-numeric: tabular-nums;
}

.leader-card__empty {
    padding: 24px 14px;
    color: rgba(0, 0, 0, 0.5);
    font-size: 0.85rem;
    text-align: center;
}

.leader-card__more {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    margin-top: auto;
    padding: 8px;
    color: #e65100;
    font-size: 0.8rem;
    font-weight: 700;
    cursor: pointer;
}

.leader-card__more:hover {
    background: #fff3ec;
}
</style>
